/**
 * Agápe Fund v5 — Registration-only Hearts Model
 *
 * Model:  Refer friends → Earn hearts → Send to masters → Masters convert to money
 *
 * How hearts are earned:
 *   - Each user registration via referral link = HEARTS_PER_REGISTRATION hearts (one-time)
 *   - Masters also earn hearts for their own referrals
 *
 * Key rules:
 *   R1. Hearts are earned only through referrals (registration)
 *   R2. Users send hearts to masters they love
 *   R3. Masters can convert received hearts → money → withdraw
 *   R4. Conversion rate: HEARTS_TO_DOLLAR_RATE hearts = $1
 *   R5. Hearts are additive (no undo in MVP)
 *   R6. Minimum withdrawal amount applies
 */

// ── Constants ──────────────────────────────────────────────

/** Agapes earned when a referred user registers (one-time) */
export const HEARTS_PER_REGISTRATION = 10;

/** Agapes awarded immediately to a new user when they register via a referral link */
export const WELCOME_BONUS = 10;

/** Hours required to activate referral agapes before they can be sent */
export const AGAPE_ACTIVATION_HOURS = 168; // 7 days = 168 hours

/** How many hearts = $1 for master conversion */
export const HEARTS_TO_DOLLAR_RATE = 100;

/** Minimum hearts to withdraw (after conversion) */
export const MIN_WITHDRAWAL_HEARTS = 500; // = $5

/** Minimum dollar amount for withdrawal */
export const MIN_WITHDRAWAL_AMOUNT = 5;

// ── Referral stats ─────────────────────────────────────────

export interface ReferralStats {
    /** Total users registered via your link */
    registrations: number;
    /** Total users who subscribed via your link */
    subscriptions: number;
    /** Total hearts earned from registrations (one-time) */
    heartsEarned: number;
}

// ── User's heart balance ───────────────────────────────────

export interface HeartsBalance {
    /** Total hearts ever earned */
    totalEarned: number;
    /** Hearts pending activation (e.g. within 7-day lock period) */
    pending: number;
    /** Hearts already sent to masters */
    totalSent: number;
    /** Hearts available to send (totalEarned - pending - totalSent) */
    available: number;
}

// ── Heart gift (user → master) ─────────────────────────────

export interface HeartGift {
    id: string;
    fromUserId: string;
    toMasterId: string;
    toMasterName: string;
    toMasterAvatar?: string;
    toMasterCategory?: string;
    heartsAmount: number;
    createdAt: string;
}

// ── Master earning record ──────────────────────────────────

export interface MasterHeartRecord {
    fromUserId: string;
    fromUserName: string;
    heartsAmount: number;
    date: string;
}

// ── Master wallet (hearts → money → withdraw) ──────────────

export type PayoutStatus = 'paid' | 'pending' | 'processing' | 'failed';

export interface MasterWallet {
    /** Total hearts received from all users */
    totalHeartsReceived: number;
    /** Hearts already converted to money */
    heartsConverted: number;
    /** Hearts available to convert */
    heartsToConvert: number;
    /** Dollar balance available for withdrawal */
    availableBalance: number;
    /** Currently processing */
    pendingBalance: number;
    /** Total $ ever withdrawn */
    totalWithdrawn: number;
    /** Whether payout method is set up */
    hasPayoutMethod: boolean;
}

export interface PayoutRecord {
    id: string;
    date: string;
    amount: number;
    heartsConverted: number;
    status: PayoutStatus;
    reference?: string;
}

// ── Fund transparency (global stats) ──────────────────────

export interface FundTransparency {
    totalUsersOnPlatform: number;
    totalMasters: number;
    totalHeartsDistributed: number;
    totalHeartsInCirculation: number;
    totalPaidOutToMasters: number;
    currentRatePerHeart: number; // $ per heart
}

// ── Master list item ──────────────────────────────────────

export interface MasterListItem {
    id: string;
    name: string;
    avatar?: string;
    category: string;
}

// ── Circle validation (unchanged) ─────────────────────────

export interface ValidatorEntry {
    userId: string;
    displayName: string;
    avatarUrl?: string;
    status: 'pending' | 'approved' | 'rejected';
    comment?: string;
    respondedAt?: string;
}

export interface CircleValidation {
    id: string;
    circleId: string;
    requesterId: string;
    circleName: string;
    circleDescription: string;
    validators: ValidatorEntry[];
    requiredApprovals: number;
    currentApprovals: number;
    status: 'pending' | 'approved' | 'rejected' | 'expired';
    createdAt: string;
    expiresAt: string;
}
